import { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router';

import BrandLogo from '@/components/BrandLogo';
import {
  PublicPracticeApiError,
  usePublicPracticeCatalog,
} from '@/features/public-practice/api';
import { parsePublicPracticeCatalogBootstrap } from '@/features/public-practice/catalog-bootstrap';
import PublicPracticeSongList from '@/features/public-practice/PublicPracticeSongList';

import '../styles/public-practice.css';

const CATALOG_TITLE = '공개 보컬 연습곡 · MR과 연습실 | 지음';
const NOT_FOUND_TITLE = '페이지를 찾을 수 없습니다 · 지음';

export default function PublicPracticeCatalogRoute() {
  const { locale = '' } = useParams<{ locale: string }>();
  const location = useLocation();

  return (
    <PublicPracticeCatalogResource
      key={`${locale}:${location.pathname}`}
      locale={locale}
      pathname={location.pathname}
    />
  );
}

function PublicPracticeCatalogResource({
  locale,
  pathname,
}: {
  locale: string;
  pathname: string;
}) {
  const initialCatalog = useMemo(
    () => parsePublicPracticeCatalogBootstrap(pathname),
    [pathname],
  );
  const query = usePublicPracticeCatalog(initialCatalog, {
    enabled: locale === 'ko',
  });
  const notFound = (
    locale !== 'ko'
    || (
      query.error instanceof PublicPracticeApiError
      && query.error.status === 404
    )
    || (query.data !== undefined && query.data.songs.length === 0)
  );

  useEffect(() => {
    document.title = notFound ? NOT_FOUND_TITLE : CATALOG_TITLE;
  }, [notFound]);

  if (notFound) {
    return (
      <div className="route-shell public-practice-route-state">
        404 — 공개 보컬 연습곡 목록을 찾을 수 없습니다.
      </div>
    );
  }

  if (!query.data) {
    return (
      <div className="route-shell public-practice-route-state">
        {query.isPending
          ? '공개 보컬 연습곡을 불러오는 중입니다.'
          : '공개 보컬 연습곡을 불러오지 못했습니다. 잠시 뒤 다시 확인해 주세요.'}
      </div>
    );
  }

  return (
    <main
      className="public-practice-catalog-page"
      data-route="public-practice-catalog"
      lang="ko"
    >
      <nav className="public-practice-breadcrumbs" aria-label="현재 위치">
        <Link className="brand-link" to="/ko" aria-label="지음">
          <BrandLogo language="ko" decorative />
        </Link>
        <span aria-hidden="true">/</span>
        <span>공개 연습곡</span>
      </nav>

      <header className="public-practice-catalog-hero">
        <p>보컬 연습곡 아카이브</p>
        <h1>공개 보컬 연습곡</h1>
        <p>
          곡별 MR과 음역, 난이도, 연습 정보를 확인하고
          {' '}필요한 연습실로 이동할 수 있습니다.
        </p>
      </header>

      {query.isError ? (
        <p className="public-practice-index-notice" role="status">
          목록을 새로 확인하지 못했습니다. 현재 내용을 계속 보여드립니다.
        </p>
      ) : null}

      <PublicPracticeSongList songs={query.data.songs} />
    </main>
  );
}
